/** @jsxImportSource @emotion/react */

'use client';

import { Box, Stack, type SxProps } from '@mui/material';
import type { MouseEventHandler, ReactNode } from 'react';

import LogoIcon from '@campaign-manager/app/ui/icons/logo-icon.svg';
import { useLayoutContext } from '@campaign-manager/feature/context/LayoutContext';
import { Button, Link, Text } from '@campaign-manager/react/components';

import * as styles from './styles';

export interface Props {
  title?: string;
  footer?: {
    label: string;
    cta:
      | {
          type: 'link';
          href: string;
          label: string;
        }
      | {
          type: 'button';
          onClick: MouseEventHandler<HTMLButtonElement>;
          label: string;
        };
  };
  children: ReactNode;
  sx?: SxProps;
}

export function EntryPageContentContainer({ title, footer, children, sx }: Props) {
  const { project } = useLayoutContext();
  return (
    <Box
      css={styles.formSide}
      sx={sx}
    >
      <Box css={styles.formContainer}>
        <Stack
          sx={{
            gap: 6,
            alignItems: 'stretch',
          }}
        >
          <Stack
            sx={{
              gap: 2.25,
              alignItems: 'start',
            }}
          >
            {project?.logo_url ? (
              <img
                src={project.logo_url}
                css={styles.customLogo}
              />
            ) : (
              <LogoIcon />
            )}
            {title && (
              <Text
                component="h1"
                size="h1"
                weight="medium"
                whiteSpace="pre-line"
              >
                {title}
              </Text>
            )}
          </Stack>
          {children}
        </Stack>
      </Box>
      {footer && (
        <Box>
          <Stack
            direction="row"
            sx={{
              gap: 2.25,
              alignItems: 'center',
            }}
          >
            {footer.label && <Text size="small">{footer.label}</Text>}
            {footer.cta.type === 'link' ? (
              <Link href={footer.cta.href}>{footer.cta.label}</Link>
            ) : (
              <Button
                color="secondary"
                onClick={footer.cta.onClick}
              >
                {footer.cta.label}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
